import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ArrowDown, ArrowDownUp, ChevronDown, ChevronRight, Plus, RefreshCw, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { cn } from '@/lib';
import { SortField, type SortSpec } from '@/domain/shared/sortOptions';
import { ArrayItemsSortMenu } from './shared/ArrayItemsSortMenu';

type OpenMap = Record<string, boolean>;

function safeParseOpenMap(raw: string | null): OpenMap | null {
  if (!raw) return null;
  try {
    const v: unknown = JSON.parse(raw);
    if (typeof v !== 'object' || v == null) return null;
    if (Array.isArray(v)) return null;

    const obj = v as Record<string, unknown>;
    const out: OpenMap = {};
    for (const [k, val] of Object.entries(obj)) {
      if (typeof val === 'boolean') out[k] = val;
    }
    return out;
  } catch {
    return null;
  }
}

export function ArrayItemsEditor<T extends { id?: string }>(props: {
  title: string;
  items: T[];
  onChange: (next: T[]) => void;

  createItem: () => T;
  getItemTitle: (item: T, idx: number) => string;

  renderItem: (args: {
    item: T;
    idx: number;
    setItem: (next: T) => void;
    update: (patch: Partial<T>) => void;
  }) => ReactNode;

  /** Optional UI/persistence */
  className?: string;
  /** Unique per tab (recommended). Example: `property-editor:${propertyId}:utilities` */
  persistKey?: string;
  /** Default expand state when nothing persisted yet */
  defaultOpen?: boolean;
  /** Optional item styling/state */
  getItemClassName?: (item: T, idx: number) => string | undefined;
  rightHeader?: (args: { item: T; idx: number }) => ReactNode;
  onRemove?: (item: T, idx: number) => void; // lets caller own delete behavior (e.g. call backend)
  canAdd?: boolean;
  canRemove?: boolean | ((item: T, idx: number) => boolean);
  canEdit?: boolean | ((item: T, idx: number) => boolean);
  sortItems?: (items: T[], sort: SortSpec) => void;
  resortItems?: () => void;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  activeSort?: SortSpec;
}) {
  const {
    title,
    items,
    onChange,
    createItem,
    getItemTitle,
    renderItem,
    className,
    persistKey,
    defaultOpen = false, // ✅ collapsed by default
    getItemClassName,
    rightHeader,
    onRemove,
    canAdd = true,
    canRemove = true,
    canEdit = true,
    sortItems,
    resortItems,
    onRefresh,
    isRefreshing,
    activeSort,
  } = props;

  const storageKey = useMemo(() => `hv:ArrayItemsEditor:${persistKey ?? title}`, [persistKey, title]);

  // key per item: prefer stable id; fallback to index
  const itemKey = (item: T, idx: number) => item.id ?? String(idx);
  const mayRemove = (item: T, idx: number) => (typeof canRemove === 'function' ? canRemove(item, idx) : canRemove);
  const mayEdit = (item: T, idx: number) => (typeof canEdit === 'function' ? canEdit(item, idx) : canEdit);

  const endRef = useRef<HTMLDivElement | null>(null);
  const topRef = useRef<HTMLDivElement | null>(null);
  const scrollToBottom = () => {
    // Let React commit the new item first, then scroll
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      });
    });
  };

  const scrollToTop = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  };

  // ✅ init from sessionStorage once
  const [open, setOpen] = useState<OpenMap>(() => safeParseOpenMap(sessionStorage.getItem(storageKey)) ?? {});

  // ✅ persist whenever open changes
  useEffect(() => {
    sessionStorage.setItem(storageKey, JSON.stringify(open));
  }, [open, storageKey]);

  // ✅ if storageKey changes (different property/template), reload
  useEffect(() => {
    const restored = safeParseOpenMap(sessionStorage.getItem(storageKey)) ?? {};
    setOpen(restored);
  }, [storageKey]);

  const isOpen = (k: string) => open[k] ?? defaultOpen;

  const allExpanded = useMemo(() => {
    if (items.length === 0) return false;
    return items.every((it, i) => isOpen(itemKey(it, i)));
  }, [items, open, defaultOpen]);

  const setAll = (value: boolean) => {
    const next: OpenMap = {};
    for (let i = 0; i < items.length; i++) {
      const k = itemKey(items[i], i);
      next[k] = value;
    }
    setOpen(next);
  };

  const toggleOne = (k: string) => setOpen((p) => ({ ...p, [k]: !isOpen(k) }));

  const addItem = () => {
    const created = createItem();
    const nextIdx = items.length;
    const key = itemKey(created, nextIdx);

    onChange([created, ...(items ?? [])]);

    // ✅ expand the newly added item
    setOpen((prev) => ({ ...prev, [key]: true }));
    scrollToTop();
  };

  const removeItem = (idx: number) => {
    const removedKey = itemKey(items[idx], idx);
    const nextItems = items.filter((_, i) => i !== idx);
    onChange(nextItems);

    // also remove its open state
    setOpen((prev) => {
      const copy: OpenMap = { ...prev };
      delete copy[removedKey];
      return copy;
    });
  };

  return (
    <div className={cn('bg-muted/25 rounded-xl border p-4', className)}>
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div className="text-lg font-semibold">{title}</div>

        <div className="flex items-center gap-2">
          {resortItems ? (
            <Button
              variant="outline"
              size="sm"
              onClick={resortItems}
              disabled={items.length === 0 || !activeSort || activeSort.field === SortField.None}
              aria-label="Re-sort"
              title="Re-sort"
            >
              <ArrowDownUp className="h-4 w-4" />
            </Button>
          ) : null}

          {sortItems ? (
            <ArrayItemsSortMenu
              disabled={items.length === 0}
              activeSort={activeSort}
              onSelect={(spec) => sortItems(items, spec)}
            />
          ) : null}

          <Button variant="outline" size="sm" onClick={() => setAll(!allExpanded)} disabled={items.length === 0}>
            {allExpanded ? (
              <>
                <ChevronDown className="mr-2 h-4 w-4" />
                Collapse all
              </>
            ) : (
              <>
                <ChevronRight className="mr-2 h-4 w-4" />
                Expand all
              </>
            )}
          </Button>

          {canAdd ? (
            <Button size="sm" onClick={addItem}>
              <Plus className="mr-2 h-4 w-4" />
              Add
            </Button>
          ) : null}

          {onRefresh ? (
            <Button variant="outline" size="icon" onClick={onRefresh} disabled={isRefreshing} aria-label="Refresh">
              <RefreshCw className={cn('h-4 w-4', isRefreshing && 'animate-spin')} />
            </Button>
          ) : null}

          <Button
            variant="outline"
            size="icon"
            onClick={scrollToBottom}
            disabled={items.length === 0}
            aria-label="Jump to bottom"
          >
            <ArrowDown className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div ref={topRef} />

      {/* Items */}
      <div className="mt-4 space-y-4">
        {items.length === 0 ? (
          <div className="bg-background/50 text-muted-foreground rounded-lg border p-6 text-sm">
            No items yet. Click <span className="font-medium">Add</span> to create one.
          </div>
        ) : null}

        {items.map((item, idx) => {
          const k = itemKey(item, idx);
          const expanded = isOpen(k);
          const itemTitle = getItemTitle(item, idx);

          const setItem = (next: T) => {
            const copy = items.slice();
            copy[idx] = next;
            onChange(copy);
          };
          const extraItemClass = getItemClassName?.(item, idx);

          const update = (patch: Partial<T>) => setItem({ ...item, ...patch });

          return (
            <Card key={k} className={cn('bg-background/60', extraItemClass)}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 py-3">
                <div className="flex min-w-0 items-center gap-2">
                  <Button variant="ghost" size="icon" onClick={() => toggleOne(k)} aria-label="Toggle expand">
                    {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  </Button>
                  <div className="truncate font-semibold">{itemTitle}</div>
                </div>

                <div className="flex items-center gap-2">
                  {rightHeader ? rightHeader({ item, idx }) : null}

                  {mayRemove(item, idx) ? (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => (onRemove ? onRemove(item, idx) : removeItem(idx))}
                      aria-label="Remove item"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  ) : null}
                </div>
              </CardHeader>

              {expanded ? (
                <CardContent className="pt-2 pb-6">
                  <fieldset disabled={!mayEdit(item, idx)} className="m-0 min-w-0 border-0 p-0">
                    {renderItem({ item, idx, setItem, update })}
                  </fieldset>
                </CardContent>
              ) : null}
            </Card>
          );
        })}
        <div ref={endRef} />
      </div>
    </div>
  );
}
